"use client";

import {
  ChevronDown,
  Globe,
  MapPin,
  Menu,
  Moon,
  Package,
  ShoppingCart,
  Sun,
  X
} from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { SearchBar } from "@/components/layout/search-bar";
import { useCartStore } from "@/components/providers/cart-store";
import { useDeliveryStore } from "@/components/providers/delivery-store";
import { categories as fallbackCategories } from "@/lib/data";

type NavCategoryItem = {
  id: string;
  name: string;
  slug: string;
  href: string;
};

type SpecialLink = {
  visible: boolean;
  label: string;
  href: string;
};

type CustomLink = {
  id: string;
  label: string;
  href: string;
  visible: boolean;
};

type HeaderNavState = {
  showAllButton: boolean;
  categories: NavCategoryItem[];
  specialLinks: {
    deals: SpecialLink;
    prime: SpecialLink;
    compare: SpecialLink;
  };
  customLinks: CustomLink[];
};

const DEFAULT_NAV: HeaderNavState = {
  showAllButton: true,
  categories: fallbackCategories.slice(0, 9).map((name) => ({
    id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    name,
    slug: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    href: `/search?category=${encodeURIComponent(name)}`
  })),
  specialLinks: {
    deals: { visible: true, label: "Today's Deals", href: "/search?deal=flash" },
    prime: { visible: true, label: "Prime", href: "/prime" },
    compare: { visible: true, label: "Compare", href: "/compare" }
  },
  customLinks: []
};

export function Navbar() {
  const searchParams = useSearchParams();
  const { data: session } = useSession();
  const { theme, setTheme } = useTheme();
  const cartCount = useCartStore((s) => s.count());
  const { pincode, city, setLocation } = useDeliveryStore();
  const [mounted, setMounted] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [lang, setLang] = useState("EN");

  // Dynamic header navigation configured by Admin
  const [navData, setNavData] = useState<HeaderNavState>(DEFAULT_NAV);

  useEffect(() => {
    setMounted(true);

    async function loadNav() {
      try {
        const res = await fetch("/api/nav");
        if (res.ok) {
          const data = await res.json();
          if (data?.nav) {
            setNavData(data.nav);
          }
        }
      } catch (err) {
        console.error("Error fetching header navigation:", err);
      }
    }

    loadNav();

    // Listen for live updates dispatched when admin updates settings
    function handleNavUpdate() {
      loadNav();
    }
    window.addEventListener("nav-updated", handleNavUpdate);
    return () => window.removeEventListener("nav-updated", handleNavUpdate);
  }, []);

  const displayCartCount = mounted ? cartCount : 0;
  const displayLocation = mounted ? `${city} ${pincode}` : "Addis Ababa 1000";

  return (
    <header className="sticky top-0 z-50 border-b border-white/20 bg-white/70 text-slate-900 shadow-lg shadow-slate-900/5 backdrop-blur-xl transition-all dark:border-white/10 dark:bg-slate-900/60 dark:text-white">
      <div className="mx-auto flex max-w-[1500px] items-center gap-2 px-2 py-2.5 sm:gap-3 sm:px-3">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="rounded-lg p-2 hover:bg-slate-900/5 dark:hover:bg-white/10 lg:hidden"
          aria-label="Open menu"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <Link href="/" className="flex shrink-0 items-center rounded-lg px-1.5 py-1 transition-colors hover:bg-slate-900/5 dark:hover:bg-white/10" aria-label="MOVA home">
          <span className="flex items-center gap-1.5">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-amazon-orange to-amber-500 text-slate-950 shadow-md">
              <ShoppingCart className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-2xl font-black tracking-tight text-transparent dark:from-white dark:to-slate-400 sm:text-[28px]">MOVA</span>
          </span>
        </Link>

        <div className="relative hidden lg:block">
          <button
            type="button"
            onClick={() => setLocationOpen((v) => !v)}
            className="flex max-w-[140px] flex-col rounded-lg px-1 py-0.5 text-left hover:bg-slate-900/5 dark:hover:bg-white/10"
          >
            <span className="flex items-center gap-0.5 text-[11px] text-slate-500 dark:text-slate-400">
              <MapPin className="h-3 w-3" />
              Deliver to
            </span>
            <span suppressHydrationWarning className="truncate text-sm font-bold">{displayLocation}</span>
          </button>
          {locationOpen && (
            <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-slate-200 bg-white p-4 text-slate-900 shadow-dropdown">
              <p className="text-sm font-bold">Choose your location</p>
              <p className="mt-1 text-xs text-slate-600">Delivery options may vary</p>
              <div className="mt-3 space-y-1">
                {[
                  { pincode: "1000", city: "Addis Ababa" },
                  { pincode: "2000", city: "Hawassa" },
                  { pincode: "3000", city: "Adama" },
                  { pincode: "4000", city: "Bahir Dar" },
                  { pincode: "5000", city: "Dire Dawa" }
                ].map((loc) => (
                  <button
                    key={loc.pincode}
                    type="button"
                    className="block w-full rounded px-2 py-2 text-left text-sm hover:bg-amber-50"
                    onClick={() => {
                      setLocation(loc.pincode, loc.city);
                      setLocationOpen(false);
                    }}
                  >
                    {loc.city} — {loc.pincode}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <SearchBar className="hidden md:flex" initialQuery={searchParams.get("q") ?? ""} />

        <div className="ml-auto flex items-center gap-0.5 sm:gap-1">
          <button
            type="button"
            onClick={() => setLang((l) => (l === "EN" ? "AM" : "EN"))}
            className="hidden items-center gap-0.5 rounded-lg px-2 py-1 text-xs hover:bg-slate-900/5 dark:hover:bg-white/10 lg:flex"
            aria-label="Change language"
          >
            <Globe className="h-4 w-4" />
            <span className="font-bold">{lang}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          <button
            type="button"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="hidden rounded-lg p-2 hover:bg-slate-900/5 dark:hover:bg-white/10 sm:inline-flex"
            aria-label="Toggle theme"
          >
            {mounted && theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <div className="group relative hidden lg:block">
            <Link
              href={
                session
                  ? session.user.role === "VENDOR"
                    ? "/vendor"
                    : session.user.role === "ADMIN1"
                      ? "/admin1"
                      : session.user.role === "ADMIN"
                        ? "/admin"
                        : "/dashboard/profile"
                  : "/login"
              }
              className="block rounded-lg px-1 py-0.5 hover:bg-slate-900/5 dark:hover:bg-white/10"
            >
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Hello, {session?.user?.name?.split(" ")[0] ?? "sign in"}</span>
              <span className="flex items-center gap-0.5 text-sm font-bold">
                Account & Lists
                <ChevronDown className="h-3 w-3" />
              </span>
            </Link>
            <div className="invisible absolute right-0 top-full w-52 translate-y-1 rounded-md border border-slate-200 bg-white p-2 text-slate-900 opacity-0 shadow-dropdown transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 dark:border-white/10 dark:bg-slate-900 dark:text-white">
              {session ? (
                <>
                  <Link className="block rounded px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/dashboard/profile">Your Account</Link>
                  <Link className="block rounded px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/dashboard/orders">Your Orders</Link>
                  <Link className="block rounded px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/dashboard/wishlist">Wishlist</Link>
                  <Link className="block rounded px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/prime">Prime</Link>
                  {session.user.role === "VENDOR" && (
                    <Link className="block rounded px-3 py-2 text-sm font-semibold text-amazon-teal hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/vendor">Vendor Dashboard</Link>
                  )}
                  {session.user.role === "ADMIN" && (
                    <Link className="block rounded px-3 py-2 text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/admin">Admin Console</Link>
                  )}
                  {session.user.role === "ADMIN1" && (
                    <Link className="block rounded px-3 py-2 text-sm font-semibold text-amazon-orange hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10" href="/admin1">Admin 1 Console</Link>
                  )}
                  <button type="button" onClick={() => signOut({ callbackUrl: "/" })} className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-amber-50 dark:hover:bg-slate-900/5 dark:bg-white/10">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link href="/login" className="block rounded bg-amazon-orange px-3 py-2 text-center text-sm font-bold text-slate-950 hover:bg-[#e07320]">Sign in</Link>
                  <p className="mt-2 px-3 text-xs text-slate-600 dark:text-slate-400">New customer? <Link href="/signup" className="text-amazon-teal hover:underline">Start here</Link></p>
                </>
              )}
            </div>
          </div>

          <Link href="/dashboard/orders" className="hidden rounded-lg px-1 py-0.5 hover:bg-slate-900/5 dark:hover:bg-white/10 lg:block">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">Returns</span>
            <span className="block text-sm font-bold">& Orders</span>
          </Link>

          <Link href="/prime" className="hidden rounded-lg px-1 py-0.5 hover:bg-slate-900/5 dark:hover:bg-white/10 xl:block">
            <span className="text-sm font-bold text-amazon-gold">prime</span>
          </Link>

          <Link
            href="/cart"
            suppressHydrationWarning
            className="relative flex items-end gap-1 rounded-lg px-1 py-0.5 hover:bg-slate-900/5 dark:hover:bg-white/10"
            aria-label={`Cart, ${displayCartCount} items`}
          >
            <div className="relative">
              <ShoppingCart className="h-8 w-8" />
              <span
                suppressHydrationWarning
                className="absolute -top-1 left-4 min-w-[18px] rounded-full bg-amazon-orange px-1 text-center text-xs font-bold text-slate-950"
              >
                {displayCartCount}
              </span>
            </div>
            <span className="hidden pb-1 text-sm font-bold sm:inline">Cart</span>
          </Link>
        </div>
      </div>

      <div className="px-2 pb-2 md:hidden">
        <SearchBar compact initialQuery={searchParams.get("q") ?? ""} />
      </div>

      {/* Category & Special Links Sub-header Bar (horizontally scrollable on all screen sizes) */}
      <div className="border-t border-slate-200/60 bg-slate-100/50 backdrop-blur-md dark:border-white/10 dark:bg-white/5">
        <nav className="mx-auto flex max-w-[1500px] items-center gap-0.5 px-2 text-xs sm:text-[13px] overflow-x-auto no-scrollbar py-1 sm:py-0">
          {/* "All" Button */}
          {navData.showAllButton && (
            <Link
              href="/search"
              className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 sm:py-1.5 font-bold hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors"
            >
              <Menu className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              All
            </Link>
          )}

          {/* Today's Deals */}
          {navData.specialLinks?.deals?.visible && (
            <Link
              href={navData.specialLinks.deals.href || "/search?deal=flash"}
              className="shrink-0 rounded-lg px-2 py-1 sm:py-1.5 text-amber-600 dark:text-amazon-gold hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors font-medium"
            >
              {navData.specialLinks.deals.label || "Today's Deals"}
            </Link>
          )}

          {/* Prime */}
          {navData.specialLinks?.prime?.visible && (
            <Link
              href={navData.specialLinks.prime.href || "/prime"}
              className="shrink-0 rounded-lg px-2 py-1 sm:py-1.5 hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors font-medium"
            >
              {navData.specialLinks.prime.label || "Prime"}
            </Link>
          )}

          {/* Admin-Selected Categories */}
          {navData.categories.map((category) => (
            <Link
              key={category.id || category.name}
              href={category.href}
              className="shrink-0 rounded-lg px-2 py-1 sm:py-1.5 hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors"
            >
              {category.name}
            </Link>
          ))}

          {/* Compare */}
          {navData.specialLinks?.compare?.visible && (
            <Link
              href={navData.specialLinks.compare.href || "/compare"}
              className="shrink-0 rounded-lg px-2 py-1 sm:py-1.5 hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors"
            >
              {navData.specialLinks.compare.label || "Compare"}
            </Link>
          )}

          {/* Custom Links */}
          {(navData.customLinks || [])
            .filter((link) => link.visible)
            .map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className="shrink-0 rounded-lg px-2 py-1 sm:py-1.5 hover:bg-slate-900/5 dark:hover:bg-white/10 transition-colors text-slate-600 dark:text-slate-300"
              >
                {link.label}
              </Link>
            ))}
        </nav>
      </div>

      {/* Mobile Drawer Menu */}
      {menuOpen && (
        <div className="border-t border-slate-200/60 bg-white/80 p-4 text-slate-900 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 dark:text-white lg:hidden max-h-[calc(100vh-110px)] overflow-y-auto">
          {/* Deliver to mobile clickable toggle */}
          <div className="mb-4 rounded-lg bg-slate-900/5 dark:bg-white/10 p-3">
            <button
              type="button"
              onClick={() => setLocationOpen((v) => !v)}
              className="flex w-full items-center justify-between text-left text-sm"
            >
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-amazon-gold shrink-0" />
                <div>
                  <span className="block text-[11px] text-slate-500 dark:text-slate-400">Deliver to</span>
                  <span className="font-bold text-slate-900 dark:text-white">{displayLocation}</span>
                </div>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            </button>

            {locationOpen && (
              <div className="mt-3 space-y-1 border-t border-slate-200/60 pt-2 dark:border-white/10">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">Select Delivery City</p>
                {[
                  { pincode: "1000", city: "Addis Ababa" },
                  { pincode: "2000", city: "Hawassa" },
                  { pincode: "3000", city: "Adama" },
                  { pincode: "4000", city: "Bahir Dar" },
                  { pincode: "5000", city: "Dire Dawa" }
                ].map((loc) => (
                  <button
                    key={loc.pincode}
                    type="button"
                    className="block w-full rounded px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-900/5 dark:text-white dark:hover:bg-white/10"
                    onClick={() => {
                      setLocation(loc.pincode, loc.city);
                      setLocationOpen(false);
                    }}
                  >
                    {loc.city} ({loc.pincode})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Status / Account Section */}
          <div className="mb-4 border-b border-slate-200/60 pb-3 dark:border-white/10">
            {session ? (
              <div className="space-y-1">
                <p className="px-1 text-xs text-slate-500 dark:text-slate-400">
                  Signed in as <strong className="text-slate-900 dark:text-white">{session.user?.name || session.user?.email}</strong>
                </p>
                <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
                  <Link
                    href="/dashboard/profile"
                    className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Your Account
                  </Link>
                  <Link
                    href="/dashboard/orders"
                    className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Your Orders
                  </Link>
                  <Link
                    href="/dashboard/wishlist"
                    className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Wishlist
                  </Link>
                  <Link
                    href="/compare"
                    className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
                    onClick={() => setMenuOpen(false)}
                  >
                    Compare
                  </Link>
                  {session.user.role === "VENDOR" && (
                    <Link
                      href="/vendor"
                      className="col-span-2 rounded bg-amazon-teal/90 px-3 py-2 text-center font-bold text-white hover:bg-amazon-teal"
                      onClick={() => setMenuOpen(false)}
                    >
                      🏪 Vendor Dashboard
                    </Link>
                  )}
                  {session.user.role === "ADMIN" && (
                    <Link
                      href="/admin"
                      className="col-span-2 rounded bg-amazon-orange/90 px-3 py-2 text-center font-bold text-slate-950 hover:bg-amazon-orange"
                      onClick={() => setMenuOpen(false)}
                    >
                      🛡️ Admin Console (Seller Central)
                    </Link>
                  )}
                  {session.user.role === "ADMIN1" && (
                    <Link
                      href="/admin1"
                      className="col-span-2 rounded bg-amazon-orange/90 px-3 py-2 text-center font-bold text-slate-950 hover:bg-amazon-orange"
                      onClick={() => setMenuOpen(false)}
                    >
                      🛡️ Admin 1 Console
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      signOut({ callbackUrl: "/" });
                    }}
                    className="col-span-2 rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 text-center text-sm font-medium hover:bg-slate-900/10 dark:hover:bg-white/20 text-red-500 dark:text-red-300"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  href="/login"
                  className="block rounded bg-amazon-orange px-3 py-2.5 text-center font-bold text-slate-950 hover:bg-[#e07320]"
                  onClick={() => setMenuOpen(false)}
                >
                  Sign in
                </Link>
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  New customer?{" "}
                  <Link
                    href="/signup"
                    className="text-amazon-gold underline"
                    onClick={() => setMenuOpen(false)}
                  >
                    Start here
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* Quick Categories & Navigation */}
          <div className="mb-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Shop by Category</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link
                href="/search"
                className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20 truncate"
                onClick={() => setMenuOpen(false)}
              >
                All categories
              </Link>
              {navData.specialLinks?.deals?.visible && (
                <Link
                  href={navData.specialLinks.deals.href || "/search?deal=flash"}
                  className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium text-amber-600 dark:text-amazon-gold hover:bg-slate-900/10 dark:hover:bg-white/20 truncate"
                  onClick={() => setMenuOpen(false)}
                >
                  {navData.specialLinks.deals.label || "Today's Deals"}
                </Link>
              )}
              {navData.categories.map((c) => (
                <Link
                  key={c.id}
                  href={c.href}
                  className="rounded bg-slate-900/5 dark:bg-white/10 px-3 py-2 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20 truncate"
                  onClick={() => setMenuOpen(false)}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          {/* App Controls: Theme & Language */}
          <div className="flex items-center gap-2 border-t border-slate-200/60 pt-3 text-sm dark:border-white/10">
            <button
              type="button"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="flex flex-1 items-center justify-center gap-2 rounded bg-slate-900/5 dark:bg-white/10 py-2.5 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
            >
              {mounted && theme === "dark" ? <Sun className="h-4 w-4 text-amazon-gold" /> : <Moon className="h-4 w-4" />}
              <span>{mounted && theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </button>
            <button
              type="button"
              onClick={() => setLang((l) => (l === "EN" ? "AM" : "EN"))}
              className="flex items-center justify-center gap-1.5 rounded bg-slate-900/5 dark:bg-white/10 px-4 py-2.5 font-medium hover:bg-slate-900/10 dark:hover:bg-white/20"
            >
              <Globe className="h-4 w-4" />
              <span>{lang}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
